import { useState } from "react";
import { emotions, roles } from "@/mocks/community";

interface Props {
  onClose: () => void;
  onPosted: () => void;
}

export default function NewPostModal({ onClose, onPosted }: Props) {
  const [content, setContent] = useState("");
  const [selectedEmotion, setSelectedEmotion] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const charCount = content.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || !selectedEmotion || !selectedRole) return;
    setSubmitting(true);
    const body = new URLSearchParams({ name, content, emotion: selectedEmotion, role: selectedRole, category });
    try {
      await fetch("https://readdy.ai/api/form/d7ckkt1noju8nk5a0tl0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch (_e) { /* silent */ }
    setSubmitting(false);
    onPosted();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <h2 className="text-lg font-extrabold text-gray-900">Share with the Community</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form data-readdy-form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
          {/* Name */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Your Name</label>
            <input name="name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Dr. Sarah or Anonymous" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-rose-300 transition-colors" />
          </div>

          {/* Role */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">I am a... *</label>
            <div className="flex flex-wrap gap-2">
              {roles.map(r => (
                <button
                  key={r.label}
                  type="button"
                  onClick={() => setSelectedRole(r.label)}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap border ${selectedRole === r.label ? "border-rose-400 bg-rose-50 text-rose-600" : "border-gray-200 text-gray-500 hover:border-rose-200 hover:text-rose-400"}`}
                >
                  <i className={r.icon}></i>{r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Emotion */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">How are you feeling? *</label>
            <div className="flex flex-wrap gap-2">
              {emotions.map(em => (
                <button
                  key={em.label}
                  type="button"
                  onClick={() => setSelectedEmotion(em.label)}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap border ${selectedEmotion === em.label ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 text-gray-500 hover:border-amber-200"}`}
                >
                  {em.emoji} {em.label}
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Category</label>
            <select name="category" value={category} onChange={e => setCategory(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-rose-300 cursor-pointer bg-white">
              <option value="">Select a category</option>
              {["Medical", "Nutrition", "Mental Health", "Clothing", "Donation", "General Help", "Baby Care", "Education"].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>

          {/* Content */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Your Message * <span className="text-gray-400 font-normal">({charCount}/500)</span></label>
            <textarea
              name="content"
              required
              value={content}
              onChange={e => { if (e.target.value.length <= 500) setContent(e.target.value); }}
              placeholder="Share what you need, what you can offer, or a message of hope for the community..."
              rows={5}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-rose-300 transition-colors resize-none"
            />
            {charCount >= 500 && <p className="text-red-500 text-xs mt-1">Maximum 500 characters reached.</p>}
          </div>

          <button
            type="submit"
            disabled={submitting || !content.trim() || !selectedEmotion || !selectedRole}
            className="bg-rose-500 hover:bg-rose-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            {submitting ? "Posting..." : "Post to Community"}
          </button>
        </form>
      </div>
    </div>
  );
}
